import { Card, Typography, Alert, CircularProgress, Container, Box, Button, Chip, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldAssetNotification, Device, EldLandmark, Organization } from "src/models";
import useModelData, { replaceById } from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";
import PageHeader from "src/components/page-header";
import { addDaysToDate, formatDateForPreview, formatDatetimeForPreview, today } from "src/utils/date-utils";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useAuthContext } from "src/auth/hooks";
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";
import Iconify from 'src/components/iconify';
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import { red } from "@mui/material/colors";
import { EldAssetNotificationModal, AssetNotificationAction } from "./eld-asset-notification-modal";

type AssetNotificationsListProps = {
  eldAssetNotifications: EldAssetNotification[],
  onEldAssetNotificationSelected: (a: EldAssetNotification) => void,
  timezone: string,
}


const AssetNotificationsList = memo(({ eldAssetNotifications, onEldAssetNotificationSelected, timezone }: AssetNotificationsListProps) => {
  const { sortedList, sortData } = useSort(eldAssetNotifications, "notificationTime", "desc");

  if (!sortedList) {
    return null;
  }
  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="notificationTime">
                Time
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="deviceType">
                Trailer
              </SortableHeader>

              <TruxTableCell>
                Landmark
              </TruxTableCell>

              <TruxTableCell align="center">
                Action
              </TruxTableCell>

              <TruxTableCell align="right">
                Notes
              </TruxTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((eldAssetNotification, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldAssetNotificationSelected(eldAssetNotification)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {formatDatetimeForPreview(eldAssetNotification.notificationTime, timezone)}
              </TruxTableCell>
              <TruxTableCell>
                {eldAssetNotification.deviceType}
              </TruxTableCell>
              <TruxTableCell>
                {eldAssetNotification.eldLandmark.name}
              </TruxTableCell>
              <TruxTableCell align="center">
                <AssetNotificationAction eldAssetNotificationType={eldAssetNotification.eldAssetNotificationType} />
              </TruxTableCell>
              <TruxTableCell align="right">
                {eldAssetNotification.notes}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldAssetNotifications?: EldAssetNotification[],
}

type PageModelBase = {
  devices?: Device[],
  eldLandmarks?: EldLandmark[],
}
export default function Page() {
  const settings = useSettingsContext();

  const [fromDate, setFromDate] = useState(addDaysToDate(today(), -2));
  const [toDate, setToDate] = useState(today());
  const [eldLandmarkId, setEldLandmarkId] = useState(0);
  const [deviceId, setDeviceId] = useState(0);

  const dataLoaderBase = useCallback(() => ({
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
    eldLandmarks: axios.get(endpoints.eldLandmarks.findAll).then(response => response.data),
  }), []);

  const dataLoader = useCallback(() => ({
    eldAssetNotifications: !fromDate || !fromDate || (!eldLandmarkId && !deviceId)
      ? []
      : axios.get(
        formatEndpoint(endpoints.eldAssetNotifications.findAll, {
          fromDate,
          toDate,
          eldLandmarkId,
          deviceId
        })
      ).then(response => response.data),
  }), [fromDate, toDate, eldLandmarkId, deviceId]);

  const {
    data: baseData,
    updateData: baseUpdateData,
    setData: baseSetData,
    error: baseError,
    loaded: baseLoaded,
    loading: baseLoading,
    setShouldRefresh: baseSetShouldRefresh,
  } = useModelData<PageModelBase>(dataLoaderBase);

  const {
    data,
    updateData,
    setData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedEldAssetNotificationModel = useModelSavingData<EldAssetNotification>(
    endpoints.eldAssetNotifications.update,
    endpoints.eldAssetNotifications.update
  );

  const updateEldAssetNotification = () => {
    selectedEldAssetNotificationModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Asset Notification");
        const eldAssetNotification = response.data as EldAssetNotification;
        updateData("eldAssetNotifications")(replaceById(data!.eldAssetNotifications!, eldAssetNotification));

        setIsOpen(false);
      }
    })
  }

  const [reportRan, setReportRan] = useState(false);

  const onEldAssetNotificationSelected = useCallback((eldAssetNotification: EldAssetNotification) => {
    selectedEldAssetNotificationModel.set(eldAssetNotification);
    setIsOpen(true);
  }, [setIsOpen, selectedEldAssetNotificationModel]);

  const { session } = useAuthContext();

  const timezone = session?.organization?.companyTimezone as string;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={baseLoading || loading} error={baseError || error} data={baseData}>
        {reportRan && <>
          <PageHeader title="Asset Notifications">
            <Button
              onClick={() => setReportRan(false)}
              variant="outlined"
              color="inherit"
              startIcon={<Iconify icon="iconamoon:search" />}
            >
              Change Filters
            </Button>
          </PageHeader>
          {data && (data.eldAssetNotifications?.length || 0) > 0 && <>
            {data.eldAssetNotifications?.length === 500 &&
              <Alert sx={{marginBottom: 1}} severity="error">
                Results are limited to 500 lines - please adjust your filters
              </Alert>
            }
            <Box sx={{marginBottom: 1, gap: 1, display: "flex"}}>
              <Chip label={<>{formatDateForPreview(fromDate)} to {formatDateForPreview(toDate)}</>} variant="outlined" />
              <Chip label={<>{baseData?.eldLandmarks?.find(l => l.id === eldLandmarkId)?.name || "All Landmarks"}</>} variant="outlined" />
              <Chip label={baseData?.devices?.find(l => l.id === deviceId)?.deviceType || "All Assets"} variant="outlined" />
            </Box>
            <AssetNotificationsList timezone={timezone} eldAssetNotifications={data.eldAssetNotifications || []} onEldAssetNotificationSelected={onEldAssetNotificationSelected} />
          </>}
          {loaded && data && data.eldAssetNotifications?.length === 0 && <NoneFoundReport />}
        </>}
        {!reportRan && <Card sx={{p: 4, display: "flex", flexDirection:"column", alignItems: "center", gap: 4, overflow: "visible"}}>
          <Typography variant="h4">
            Asset Notifications
          </Typography>

          <Box maxWidth="300px" width="100%" display="flex" flexDirection="column" alignItems="center" gap={2}>

            {!reportRan && !loading && <>
              <Box width="100%">
                <Typography component="div" fontWeight={700}>From Date</Typography>
                <TruxSimpleDateField value={fromDate} onChange={d => setFromDate(d!)} limitWidth={false} />
              </Box>

              <Box width="100%">
                <Typography component="div" fontWeight={700}>To Date</Typography>
                <TruxSimpleDateField value={toDate} onChange={d => setToDate(d!)} limitWidth={false} />
              </Box>

              <Box width="100%">
                <Typography component="div" fontWeight={700}>Landmark</Typography>
                <TruxAutocompleteField
                  freeSolo={false}
                  options={baseData?.eldLandmarks?.map(eldLandmark => ({
                    label: eldLandmark.name,
                    id: eldLandmark.id
                  })) || []}
                  onClear={() => {
                    setEldLandmarkId(0);
                  }}
                  onChange={(value) => {
                    setEldLandmarkId((val) => value == null ? val : value?.id);
                  }}
                  noOptionsText="No Landmarks found"
                  selected={{
                    id: eldLandmarkId,
                    label: baseData?.eldLandmarks?.find(l => l.id === eldLandmarkId)?.name || "None",
                  }}
                />
              </Box>

              <Box width="100%">
                <Typography component="div" fontWeight={700}>Asset</Typography>
                <TruxAutocompleteField
                  freeSolo={false}
                  options={baseData?.devices?.map(device => ({
                    label: device.deviceType,
                    id: device.id
                  })) || []}
                  onClear={() => {
                    setDeviceId(0);
                  }}
                  onChange={(value) => {
                    setDeviceId((val) => value == null ? val : value?.id);
                  }}
                  noOptionsText="No Assets found"
                  selected={{
                    id: deviceId,
                    label: baseData?.devices?.find(l => l.id === deviceId)?.deviceType || "None",
                  }}
                />
              </Box>

              <Button variant="contained" color="success" sx={{mt:2}} startIcon={<Iconify icon="iconamoon:search" />} onClick={() => {
                if (!eldLandmarkId && !deviceId) {
                  enqueueSnackbar("Please select a Landmark or a Device", {variant: "error"});
                  return;
                }
                setShouldRefresh(true);
                setReportRan(true);
              }}>
                Search
              </Button>
            </>}
          </Box>
        </Card>}
      </DisplayPageData>

     {data && <EldAssetNotificationModal
        timezone={timezone}
        selectedEldAssetNotificationModel={selectedEldAssetNotificationModel}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        updateEldAssetNotification={updateEldAssetNotification}
      />}
    </Container>
    // <Container maxWidth={settings.themeStretch ? false : 'lg'}>
    //   <DisplayPageData loading={loading} error={error} data={data}>
    //     <PageHeader title="Asset Notifications" />

    //     <PageFilterLoader pageFilterData={pageFilterData} onRun={() => {
    //       setShouldRefresh(true);
    //     }} />


    //   </DisplayPageData>

    // </Container >
  );
}
